import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { TableKit } from '@tiptap/extension-table';

interface RichTextEditorProps {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
}

function ToolbarButton({
    label,
    active,
    onClick,
}: {
    label: string;
    active?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                active
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-100'
            }`}
        >
            {label}
        </button>
    );
}

export default function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
            }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
            }),
            TableKit.configure({
                table: { resizable: false },
            }),
        ],
        content: value || '',
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none min-h-[140px] px-3 py-2 text-sm text-slate-800 focus:outline-none',
            },
        },
        onUpdate: ({ editor: instance }) => {
            const html = instance.getHTML();
            const empty = html === '' || html === '<p></p>';
            onChange(empty ? '' : html);
        },
    });

    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const next = value || '';
        const normalize = (html: string) => (html === '<p></p>' ? '' : html);
        if (normalize(current) === normalize(next)) return;
        editor.commands.setContent(next, { emitUpdate: false });
    }, [value, editor]);

    const setLink = () => {
        if (!editor) return;
        const previous = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('Link URL', previous || 'https://');
        if (url === null) return;
        if (url.trim() === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    return (
        <div className="rounded-lg border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500">
            <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-1.5 py-1">
                <ToolbarButton
                    label="B"
                    active={editor?.isActive('bold')}
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                />
                <ToolbarButton
                    label="I"
                    active={editor?.isActive('italic')}
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                />
                <ToolbarButton
                    label="H2"
                    active={editor?.isActive('heading', { level: 2 })}
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                />
                <ToolbarButton
                    label="H3"
                    active={editor?.isActive('heading', { level: 3 })}
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                />
                <ToolbarButton
                    label="List"
                    active={editor?.isActive('bulletList')}
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                />
                <ToolbarButton
                    label="1."
                    active={editor?.isActive('orderedList')}
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                />
                <ToolbarButton
                    label="Quote"
                    active={editor?.isActive('blockquote')}
                    onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                />
                <ToolbarButton
                    label="Link"
                    active={editor?.isActive('link')}
                    onClick={setLink}
                />
                <ToolbarButton
                    label="Table"
                    active={editor?.isActive('table')}
                    onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                />
                {editor?.isActive('table') && (
                    <>
                        <span className="mx-1 h-3 w-px bg-slate-200" aria-hidden />
                        <ToolbarButton
                            label="+Row"
                            onClick={() => editor.chain().focus().addRowAfter().run()}
                        />
                        <ToolbarButton
                            label="+Col"
                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                        />
                        <ToolbarButton
                            label="-Row"
                            onClick={() => editor.chain().focus().deleteRow().run()}
                        />
                        <ToolbarButton
                            label="-Col"
                            onClick={() => editor.chain().focus().deleteColumn().run()}
                        />
                        <ToolbarButton
                            label="Delete table"
                            onClick={() => editor.chain().focus().deleteTable().run()}
                        />
                    </>
                )}
                <ToolbarButton label="Undo" onClick={() => editor?.chain().focus().undo().run()} />
                <ToolbarButton label="Redo" onClick={() => editor?.chain().focus().redo().run()} />
            </div>
            <div className="relative">
                {placeholder && editor?.isEmpty && (
                    <p className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400">
                        {placeholder}
                    </p>
                )}
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}
